import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ScienceIcon from '@mui/icons-material/Science';
import SchoolIcon from '@mui/icons-material/School';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LanguageIcon from '@mui/icons-material/Language';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDomain, DomainItem } from '@/contexts/domainContext';

const getDomainIcon = (domainId: string, size = 18) => {
  switch (domainId) {
    case 'biopharm':
      return <ScienceIcon sx={{ fontSize: size, color: '#10B981' }} />;
    case 'education':
      return <SchoolIcon sx={{ fontSize: size, color: '#8B5CF6' }} />;
    case 'itsupport':
      return <SupportAgentIcon sx={{ fontSize: size, color: '#0EA5E9' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: size, color: '#6366F1' }} />;
  }
};

const getDomainBadgeColor = (domainId: string) => {
  switch (domainId) {
    case 'biopharm':
      return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.25)', text: '#059669' };
    case 'education':
      return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.25)', text: '#7C3AED' };
    case 'itsupport':
      return { bg: 'rgba(14, 165, 233, 0.1)', border: 'rgba(14, 165, 233, 0.25)', text: '#0284C7' };
    default:
      return { bg: 'rgba(99, 102, 241, 0.1)', border: 'rgba(99, 102, 241, 0.25)', text: '#4F46E5' };
  }
};

export const DomainSwitcher: React.FC = () => {
  const { activeDomainId, activeDomain, availableDomains, switchDomain, launchDomainPortal } = useDomain();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectDomain = (domainId: string) => {
    switchDomain(domainId);
    handleClose();
  };

  const badgeColor = getDomainBadgeColor(activeDomainId);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Button
        onClick={handleClick}
        startIcon={getDomainIcon(activeDomainId, 19)}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '13px',
          px: 1.75,
          py: 0.6,
          borderRadius: '18px',
          bgcolor: badgeColor.bg,
          border: `1px solid ${badgeColor.border}`,
          color: badgeColor.text,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(badgeColor.bg, 0.2),
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box component="span" sx={{ maxWidth: { xs: 90, sm: 160 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeDomain.name}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1.5,
            width: 340,
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            p: 1,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Active Domain Workspace
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11.5px', mt: 0.25 }}>
            Filters pipelines, data sources, agents, and tool capabilities.
          </Typography>
        </Box>

        <Divider sx={{ my: 0.75 }} />

        {availableDomains.map((domain) => {
          const isSelected = domain.id === activeDomainId;
          const colors = getDomainBadgeColor(domain.id);

          return (
            <MenuItem
              key={domain.id}
              onClick={() => handleSelectDomain(domain.id)}
              selected={isSelected}
              sx={{
                borderRadius: '8px',
                my: 0.5,
                p: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                '&.Mui-selected': {
                  bgcolor: colors.bg,
                  '&:hover': { bgcolor: alpha(colors.bg, 0.2) },
                },
              }}
            >
              <Box sx={{ mt: 0.25 }}>{getDomainIcon(domain.id, 22)}</Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 800 : 600, fontSize: '13.5px', color: isSelected ? colors.text : 'text.primary' }}>
                    {domain.name}
                  </Typography>
                  {isSelected && <CheckIcon sx={{ fontSize: 16, color: colors.text }} />}
                </Box>
                {domain.description && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '11px', mt: 0.25 }}>
                    {domain.description}
                  </Typography>
                )}

                {/* If domain has a dedicated portal (e.g. BioPharm), show quick launch button */}
                {domain.portalUrl && (
                  <Box sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
                    <Chip
                      size="small"
                      icon={<OpenInNewIcon style={{ fontSize: 12 }} />}
                      label="Launch Dedicated Portal"
                      onClick={() => {
                        handleClose();
                        launchDomainPortal(domain.id);
                      }}
                      sx={{
                        fontSize: '10.5px',
                        height: 22,
                        cursor: 'pointer',
                        bgcolor: 'rgba(16, 185, 129, 0.12)',
                        color: '#059669',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontWeight: 650,
                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default DomainSwitcher;
